import type { LeadsFilterCategory } from "../types/leadsFilterCategory";
import type { LeadsSearchFilterKey } from "../types/leadsSearchFilters.dto";

/**
 * THE display order of the leads panel. Position in this list *is* the order: there is no number to pick,
 * and inserting a filter means putting its anchor where you want it to appear.
 *
 * It spans both leads configs, because `mergeFiltersConfigs` sorts one global list — the CRM category is
 * built separately (`crmFiltersConfig`) but has always been interleaved between Company and Signals, and a
 * per-config numbering scheme cannot express that.
 *
 * Two anchors are *groups* whose membership is only known at runtime (one filter per signal type; one per
 * CRM custom field). They fan out into the slots reserved after them, so a group can grow without
 * colliding with the next filter.
 */
const leadsFilterOrderAnchors = [
    "countries",
    "companyDomains",
    /** `crmScope` followed by the CRM custom filters. */
    "crm",
    /** One filter per signal type, ordered by `signalsService.orderedSignalTypes`. */
    "signals",
    "adSpendVolume",
    "companyHeadquarter",
    "employeeHeadcount",
    "leadScores",
    "businessModels",
    "annualRevenue",
    "totalFundingAmount",
    "industryFilter",
    "industryTaxonomy",
    "industryCodesFilter",
    "siteTags",
    "websiteType",
    "technologies",
    "onlineRevenue",
    "monthlyAverageTransactions",
    "conversionRate",
    "topLevelDomain",
    "keywords",
    "incomingTraffic",
    "internationalFilters",
    "topGeoCountries",
    "engagement",
    "marketingChannels",
    "usdSpend",
    "audienceGenderSplits",
    "audienceAgeGroups",
    "adSpendChannels",
    "displayAdsFormat",
    "staticListsToExclude",
    "suppressionListIds",
    "salesforce",
    "hubspot",
    "dynamics",
] as const satisfies Array<Partial<LeadsSearchFilterKey> | LeadsFilterCategory>;

export type LeadsFilterOrderAnchor = (typeof leadsFilterOrderAnchors)[number];

/** Room for a group anchor to fan out (signal types, CRM custom fields) without reaching the next anchor. */
const ORDER_SLOTS_PER_ANCHOR = 1000;

const orderByAnchor: ReadonlyMap<LeadsFilterOrderAnchor, number> = new Map(
    leadsFilterOrderAnchors.map((anchor, index) => [anchor, index * ORDER_SLOTS_PER_ANCHOR]),
);

/** The `order` for a single filter, or the base of a group anchor's reserved slots. */
export const orderOf = (anchor: LeadsFilterOrderAnchor): number => orderByAnchor.get(anchor) as number;

/** The `order` for the n-th member of a group anchor. */
export const orderOfGroupMember = (anchor: LeadsFilterOrderAnchor, index: number): number => orderOf(anchor) + index;
